import { createAction, props } from '@ngrx/store';
import { type PartialDeep } from 'type-fest';

import {
  type CanvasElementId,
  type CanvasNamespaceId,
  type ColumnId,
  type ConnectionChain,
  type ElementQueryProps,
  type LayoutData,
  type RuntimeEvent,
  type RuntimeUiEventPayload,
  type WrapperConnectionData,
} from '@blaze-runtime/engine-domain';
import {
  type ConnectionId,
  type ConnectionInfo,
  type LogicalQueryNode,
  type PageInfo,
  type PageType,
  type WorkflowNodeId,
} from '@blaze-shared/api-contracts-domain';
import { type Pagination } from '@blaze-shared/datasets-domain';
import {
  type ActionKind,
  type CanvasElementInputBinding,
  type ChangePending,
  type ElementMetaData,
  type ElementOutputUpdateSource,
  type FormContext,
  type FormFormulasRefreshTriggerPayload,
  type FormState,
  type FormStateContainer,
  type RenderElementContext,
  type RenderElementDataRowContext,
  type RuntimeContext,
  type UpdateFormRowStatePayload,
} from '@blaze-shared/elements-domain';
import { type PageContext } from '@blaze-shared/projects-domain';
import {
  type DatasetSchema,
  type ElementSchema,
  type RuntimeEventType,
  type WorkflowSchema,
} from '@blaze-shared/schema-domain';
import {
  type Connection,
  type LegacyWorkflowError,
  type NamespaceId,
  type RuntimeElement,
  type Workflow,
  type WorkflowFormModel,
  type WorkflowNode,
  type WorkflowNodeDiagramPosition,
  type WorkflowPhase,
  type WorkflowPhaseInfo,
  type WorkflowSchemas,
} from '@blaze-shared/workflow-domain';

import { type WebSocketConfig } from './web-socket-config';

const TAG = '[Runtime]';

export const alertRemove = createAction(
  `${TAG} Alert remove`,
  props<{ alertId: string; elementId: string; path: string }>(),
);

export const autostartNamespace = createAction(
  `${TAG} Autostart namespace connections`,
  props<{ ns: NamespaceId; nsType: PageType }>(),
);

export const applyWorkflow = createAction(
  `${TAG} Apply workflow`,
  props<{
    event: RuntimeEvent;
    input: any; // Main (input1) workflow input
    inputsData?: { [key: string]: any };
    pageContext?: PageContext;
    inputSchema: WorkflowSchema;
    socketConfig?: WebSocketConfig;
    target: RuntimeElement | undefined; // Runtime element to which workflow provides output,
    workflow: Workflow;
    workflowExecutionId: string;
    debugMode?: boolean;
  }>(),
);

export interface ApplyWorkflowNodePayload {
  affectedDatasetIds?: string[];
  // Auxiliary inputs that will be used by at least one of the workflow nodes. This includes data from all connections
  // which are connected to the Workflow. Maps input name to the data, eg. { OrderTable: {...}, CustomerSelect: '' }
  ctx: RuntimeContext;
  debugMode?: boolean;
  elementId?: string; // Element ID of initialized element. Not specified if it's not initialization workflow.
  event: RuntimeEvent;
  isWorkflowCancelled?: boolean;
  // Main input data for the node. Can be either output from another runtime element or output from another node.
  nodeInput: any;
  // Custom output name which should be used instead of $$input1 (for example $$currentRow from ForEach node)
  outputName?: string;
  prevNodeId?: WorkflowNodeId; // ID of the node that just finished executing
  resumeToken?: string; // Token used to resume wf execution on the server
  schema: WorkflowSchema; // Schema of nodeInput
  target: RuntimeElement | undefined; // Runtime element to which workflow provides output
  userInputs: Record<string, any> | null; // User inputs from a dialog showed before node execution
  workflow: Workflow | null;
  workflowExecutionId: string;
  workflowNode: WorkflowNode | null;
}

export const applyWorkflowNode = createAction(
  `${TAG} Apply workflow node`,
  props<ApplyWorkflowNodePayload>(),
);

export const connectionsComplete = createAction(`${TAG} Connections complete`);

export const connectionsAdd = createAction(
  `${TAG} Connections add`,
  props<{
    ns: CanvasNamespaceId;
    eventType: RuntimeEventType;
    source: RuntimeElement;
    target: RuntimeElement;
  }>(),
);

export const connectionAdd = createAction(
  `${TAG} Connection add`,
  props<{ connection: Connection | Connection[] }>(),
);

export const connectionsAddOrReplace = createAction(
  `${TAG} Connections add or replace`,
  props<{ connections: Connection[] }>(),
);

export const connectionsReplace = createAction(
  `${TAG} Connections replace`,
  props<{ ns: NamespaceId; connections: Connection[] }>(),
);

export const connectionRemove = createAction(
  `${TAG} Connection remove`,
  props<{ id: ConnectionId }>(),
);

export const connectionStepComplete = createAction(
  `${TAG} Workflow complete`,
  props<{
    event: RuntimeEvent;
    // only specified for workflow connection step
    phase?: WorkflowPhase;
    phaseInfo?: WorkflowPhaseInfo;
    workflow?: Workflow | null;
    workflowExecutionId: string | null; //  null in case of UI targetting events
    outputData?: any;
    workflowSchema?: WorkflowSchema;
    updateSource?: ElementOutputUpdateSource;
  }>(),
);

export const clearOutputsAndSchema = createAction(
  `${TAG} Clear workflow outputs`,
  props<{ accountSchemaChanged?: boolean; userSchemaChanged?: boolean }>(),
);

export const formSubmitActionUpdated = createAction(
  `${TAG} Form submit action updated`,
  props<{
    workflow: Workflow;
    formConnection: Connection;
    buttonConnection: Connection | undefined;
  }>(),
);

export type FormGroupSubmitActionUpdate = {
  workflow: Workflow;
  buttonConnection: Connection | undefined;
  formsStatusConnection: Connection | undefined;
  formsConnections: Connection[];
};

export const formGroupSubmitActionUpdated = createAction(
  `${TAG} Form Group submit action updated`,
  props<FormGroupSubmitActionUpdate>(),
);

export const executeUiWorkflow = createAction(
  `${TAG} Execute UI workflow`,
  props<{ elementId: string }>(),
);

export const uiWorkflowExecuted = createAction(
  `${TAG} UI workflow executed`,
  props<{ elementId: string }>(),
);

export const executeWorkflow = createAction(
  `${TAG} Execute workflow`,
  props<{
    workflowId: string;
    /** BL-2498 - When workflow changes it needs to be run regardless of {@link Workflow.preventAutoStart} */
    ignorePreventAutostart?: boolean;
    skipNamespaceChange?: boolean /** whether wf should execute without modifying namespace in store */;
  }>(),
);

export const markPendingUi = createAction(
  `${TAG} Mark pending Ui`,
  props<{ elementIds: CanvasElementId[]; changePending: ChangePending }>(),
);

export const markPendingUiWorkflow = createAction(
  `${TAG} Mark pending Ui Workflow`,
  props<{ event: RuntimeEvent; workflowId: string }>(),
);

export const markPendingUiFinish = createAction(
  `${TAG} Mark pending Ui - finish`,
  props<{ elementIds: CanvasElementId[]; changePending: ChangePending }>(),
);

export const observeExternalAccounts = createAction(
  `${TAG} Observe external accounts`,
  props<{ datasetId: string }>(),
);

export const prefilledPageLayoutGenerated = createAction(
  `${TAG} Prefilled page layout generated`,
  props<{
    workflow: Workflow | undefined;
    connections: Connection[] | undefined;
  }>(),
);

export const queueConnectionChain = createAction(
  `${TAG} Queue connection chain`,
  props<{ ns: CanvasNamespaceId; connectionChain: ConnectionChain }>(),
);

export const queueConnections = createAction(
  `${TAG} Queue connections`,
  props<{
    connections: Connection[];
    ns: CanvasNamespaceId;
    skipNamespaceChange?: boolean /** whether wf should execute without modifying namespace in store */;
  }>(),
);

export const resumeWorkflowNode = createAction(
  `${TAG} Resume workflow node on server`,
  props<ApplyWorkflowNodePayload>(),
);

export const runtimeEvent = createAction(
  `${TAG} Dispatch runtime event`,
  props<{ event: RuntimeEvent }>(),
);

export const runtimeUiEvent = createAction(
  `${TAG} Dispatch runtime event from the UI layer`,
  props<RuntimeUiEventPayload>(),
);

export const runtimeInfoFetched = createAction(
  `${TAG} Runtime information fetched`,
  props<{
    projectId: string;
    schemas: { [key: string]: DatasetSchema };
    workflows: Workflow[];
  }>(),
);

export const hydrateRuntime = createAction(`${TAG} Hydrate runtime from localStorage`);

export const storeRuntimeElementSchema = createAction(
  `${TAG} Store runtime element schema`,
  props<{
    element: RuntimeElement;
    schema: ElementSchema | WorkflowSchema;
    isPaginated: boolean;
    eventType?: RuntimeEventType;
  }>(),
);

export const updateRuntimeUiOutput = createAction(
  `${TAG} Update runtime Ui output`,
  props<{
    elementId: CanvasElementId;
    eventType: RuntimeEventType;
    data: any;
    updateSource?: ElementOutputUpdateSource;
  }>(),
);

export const clearRuntimeUiOutputs = createAction(
  `${TAG} Clear runtime Ui outputs`,
  props<{
    eventType: RuntimeEventType;
    updateSource?: ElementOutputUpdateSource;
    elementIds: CanvasElementId[];
  }>(),
);

export const updateWrapperConnections = createAction(
  `${TAG} Update wrapper connections`,
  props<{
    elementId: CanvasElementId;
    inputWorkflow: Workflow;
    connectionData: WrapperConnectionData[];
  }>(),
);

export const addRelatedElementWorkflowAndConnections = createAction(
  `${TAG} Add related element workflow and connections`,
  props<{
    workflow: Workflow;
    connectionToWorkflow: Connection;
    connectionFromWorkflow: Connection;
  }>(),
);

export const applyAiGeneratedWorkflow = createAction(
  `${TAG} Apply AI generated workflow`,
  props<{
    elementId: CanvasElementId;
    bindingKey: CanvasElementInputBinding;
    workflow: Workflow;
    dataSourceName: string;
    inputConnections: Connection[];
  }>(),
);

export const schemasUpdate = createAction(
  `${TAG} Schemas update`,
  props<{ schemas: { [key: string]: DatasetSchema } }>(),
);

export const schemasUpdateForLayout = createAction(
  `${TAG} Schemas update for layout`,
  props<{ schemas: { [key: string]: DatasetSchema }; layout: LayoutData }>(),
);

export const editElementWorkflow = createAction(
  `${TAG} Edit element workflow`,
  props<{
    elementId?: CanvasElementId | null;
    inputData?: Record<string, unknown>;
    nodeId?: string;
    ns?: NamespaceId;
    showOldDialog?: boolean;
    workflowId: string;
  }>(),
);

export const createWorkflowFunction = createAction(
  `${TAG} Create workflow function`,
  props<{ pickedNodes: WorkflowNode[]; sourceWfModel: WorkflowFormModel; projectId: string }>(),
);

export const editWorkflowFunction = createAction(
  `${TAG} Edit workflow function`,
  props<{ workflowId: string; isDraftFunction?: boolean }>(),
);

export const subscribeToWebSocketWfRefresh = createAction(
  `${TAG} Subscribe to websocket workflow refresh messages`,
  // subscription id follows the format: .<worklowId>_<userId>_<channelId>.<SubscriptionAction>
  props<{ subscriptionIds: string[]; socketConfig: WebSocketConfig }>(),
);

export const workflowsSet = createAction(
  `${TAG} Workflows set`,
  props<{ workflows: Workflow[] }>(),
);

export const workflowAdd = createAction(`${TAG} Workflow add`, props<{ workflow: Workflow }>());

export const workflowsAdd = createAction(
  `${TAG} Workflows add`,
  props<{ workflows: Workflow[] }>(),
);

export const workflowUpdate = createAction(
  `${TAG} Workflow update`,
  props<{ workflow: Workflow; elementId?: string | null }>(),
);

export const upsertWorkflow = createAction(
  `${TAG} Upsert workflow`,
  props<{ workflow: Workflow }>(),
);

export const workflowsUpdate = createAction(
  `${TAG} Workflows update`,
  props<{ workflows: Workflow[] }>(),
);

export const workflowRemove = createAction(
  `${TAG} Workflow remove`,
  props<{
    // when removing webhook workflow, connectors need to be first updated to remove reference to the workflow
    updateConnectors?: boolean;
    triggerLayoutSync?: boolean;
    workflowId: string;
  }>(),
);

export const workflowRemoveSuccess = createAction(
  `${TAG} Workflow remove success`,
  props<{ workflowId: string }>(),
);

export const workflowsRemoved = createAction(
  `${TAG} Workflows removed`,
  props<{ workflowIds: string[] }>(),
);

export const workflowEditDialogClosed = createAction(
  `${TAG} Workflow edit dialog closed`,
  props<{
    workflowId: string;
    projectId: string;
    workflow?: Workflow;
    isDraftFunction?: boolean;
  }>(),
);

export const initializeElementQuery = createAction(
  `${TAG} Initialize element query`,
  props<{ elementId: string; props: ElementQueryProps }>(),
);

export const storeElementPaginationTokens = createAction(
  `${TAG} Store element pagination tokens`,
  props<{
    elementId?: string;
    paginationRequest?: Pagination | null;
    paginationResponse?: Pagination | null;
  }>(),
);

export const removeElementQuery = createAction(
  `${TAG} Remove element query`,
  props<{ elementId: string }>(),
);

export const updateElementQueryNode = createAction(
  `${TAG} Update element query node`,
  props<{ elementId: CanvasElementId; query: LogicalQueryNode | undefined }>(),
);

export const updateFormState = createAction(
  `${TAG} Update form state`,
  props<{ formElementId: string; formState: FormState<FormStateContainer> }>(),
);

export const updateFormContext = createAction(
  `${TAG} Update form context`,
  props<{ formElementId: string; formContext: FormContext | undefined }>(),
);

export const updateFormRowState = createAction(
  `${TAG} Update form row state`,
  props<UpdateFormRowStatePayload>(),
);

export const clearFormState = createAction(
  `${TAG} Clear form state`,
  props<{ formElementId: string }>(),
);

// marks a form flag, that the submit button has been clicked
export const updateFormSubmitted = createAction(
  `${TAG} Update form submitted`,
  props<{ formElementId: string }>(),
);

export const updateFormPosted = createAction(
  `${TAG} Update form posted`,
  props<{ formElementId: string }>(),
);

// reset validation
export const updateFormReset = createAction(
  `${TAG} Update form reset`,
  props<{ formElementId: string }>(),
);

export const updateFormCompleted = createAction(
  `${TAG} Update form completed`,
  props<{ formElementId: string }>(),
);

// handle post-submit action, when the submit WF has completed
export const formSubmitComplete = createAction(
  `${TAG} Form submit complete`,
  props<{ workflow: Workflow | null }>(),
);

export const formFormulasRefreshTrigger = createAction(
  `${TAG} Form formulas refresh trigger`,
  props<FormFormulasRefreshTriggerPayload>(),
);

export const formValidationTrigger = createAction(
  `${TAG} Form validation trigger`,
  props<{ formElementId: CanvasElementId }>(),
);

export const tryDeleteActionDialogPage = createAction(
  `${TAG} Try delete action dialog page`,
  props<{ workflowId?: string; connectionId?: string; dialogId?: string }>(),
);

export const workflowFieldValueErrorAdd = createAction(
  `${TAG} Workflow field value error add`,
  props<LegacyWorkflowError>(),
);

export const workflowFieldValueErrorRemove = createAction(
  `${TAG} Workflow field value error remove`,
  props<{ workflowId: string; fieldId?: string }>(),
);

export const updateElementMeta = createAction(
  `${TAG} Update element meta`,
  props<{ elementId: string; meta: PartialDeep<ElementMetaData> }>(),
);

/** Used for Wizard and Tabs */
export const updateElementMetaStepKill = createAction(
  `${TAG} Update element meta - step kill`,
  props<{
    elementId: CanvasElementId;
    slotId: ColumnId;
  }>(),
);

export const showNewWorkflowNodeAiPrompt = createAction(
  `${TAG} Show new workflow node ai prompt`,
  props<{
    workflow: Workflow;
    position: WorkflowNodeDiagramPosition | undefined;
    schemas: WorkflowSchemas | undefined;
  }>(),
);

export const workflowAiPromptCompleted = createAction(
  `${TAG} Workflow ai prompt completed`,
  props<{ workflow: Workflow }>(),
);

export const workflowEditorDialogClosed = createAction(`${TAG} Workflow editor dialog closed`);

export const showContainerActionAiPrompt = createAction(
  `${TAG} Show container action ai prompt`,
  props<{
    workflow: Workflow | undefined;
    elementId: string;
    actionPath: string;
    actionGenerationType?: ActionKind;
  }>(),
);

export const containerActionAiPromptCompleted = createAction(
  `${TAG} Container action ai prompt completed`,
  props<{
    elementId: CanvasElementId;
    actionPath: string;
    workflow: Workflow;
    connections: ConnectionInfo[];
    icon?: string;
    label?: string;
    visibility?: string;
  }>(),
);

/** Render element context */
export const setRenderElementContext = createAction(
  `${TAG} Set render element context`,
  props<{ elementId: CanvasElementId; context: RenderElementContext | undefined }>(),
);

export const clearRenderElementContext = createAction(
  `${TAG} Clear render element context`,
  props<{ elementId: CanvasElementId | undefined; rowContext?: RenderElementDataRowContext }>(),
);

/**
 * Used for clearing the state before opening a dialog or a drawer.
 * @param pageId - ID of the dialog/drawer
 */
export const clearDialogLikePageOutputs = createAction(
  `${TAG} Clear outputs for dialog-like page`,
  props<{ pageId: string }>(),
);

export const convertToDialog = createAction(
  `${TAG} Convert to dialog`,
  props<{ page: PageInfo }>(),
);

export const convertToDrawer = createAction(
  `${TAG} Convert to drawer`,
  props<{ page: PageInfo }>(),
);

export const convertToPage = createAction(`${TAG} Convert to page`, props<{ page: PageInfo }>());
